import Item from '../../canvas-timeline/canvas_timeline_item';
import {
	areItemDatesValid,
	createCanvasTimelineDate,
	DRAGGABLE_TYPE,
	drawRectangle,
	getMomentFromCanvasTimelineDate,
	getTextColor,
	getTrimmedText,
	GROUP_TYPE,
	ITEM_TYPE,
} from '../../canvas-timeline/canvas_timeline_util';
import Util from '../../../../forecast-app/shared/util/util';
import {getAllocationTotal} from '../../../scheduling/project_allocation_logic';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import * as tracking from '../../../../tracking';
import {updateAllocation} from '../../../scheduling/scheduling_mutations';
import {hasPermission} from '../../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../Permissions';
import {dispatch, EVENT_ID} from "../../../../containers/event_manager";

export class ProjectAllocationItem extends Item {
	constructor(pageComponent, allocation) {
		super(ITEM_TYPE.PEOPLE_SCHEDULING_PROJECT_ALLOCATION);

		this.pageComponent = pageComponent;
		this.updateWithAllocation(allocation);
	}

	isValid() {
		return this.data && this.data.allocation;
	}

	updateWithAllocation(allocation) {
		const data = this.pageComponent.getData();
		const {intl} = this.pageComponent.props;
		const {projectId, projectGroupId, idleTimeId} = allocation;
		const person = data.persons.find(person => person.id === allocation.personId);
		const project = projectId ? data.projects.find(project => project.id === projectId) : null;
		let isDisabled = false;
		let isDoneOrHalted = false;
		if (project) {
			if (!data.company.isUsingProjectAllocation) return;
			// If person is not on project, dont add ProjectAllocationItem
			if (
				!data.projectPersons.find(
					projectPerson => projectPerson.personId === person.id && projectPerson.projectId === project.id
				)
			) {
				return;
			}
			isDisabled =
				!hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL) &&
				!data.projectPersons.find(
					projectPerson =>
						projectPerson.personId === data.viewer.actualPersonId && projectPerson.projectId === project.id
				);
			isDoneOrHalted = ['DONE', 'HALTED'].includes(project.status);
			allocation.displayName = project.name;
		}
		//Do not show allocations for connected projects that use task allocations
		if (projectGroupId) {
			if (!data.company.isUsingProjectAllocation) return;
			const projectGroupProject = data.projects.find(project => project.projectGroupId === projectGroupId);
			const projectGroup = data.projectGroups.find(projectGroup => projectGroup.id === projectGroupId);
			if (projectGroupProject) {
				isDisabled =
					!hasPermission(PERMISSION_TYPE.PROJECTS_READ_ALL) &&
					!data.projectPersons.find(
						projectPerson =>
							projectPerson.personId === data.viewer.actualPersonId &&
							projectPerson.projectId === projectGroupProject.id
					);
				isDoneOrHalted = data.projects
					.filter(project => project.projectGroupId === projectGroupId)
					.every(project => ['HALTED', 'DONE'].includes(project.status));
				allocation.displayName = projectGroup ? projectGroup.name : '';
			}
		}
		const groupId = projectId
			? `${person.id}-${projectId}`
			: projectGroupId
			? `${person.id}-${projectGroupId}`
			: `${person.id}-2`;
		const color = project ? project.projectColor : projectGroupId ? allocation.projectGroupColor : '#d2d2d2';
		const startDate = createCanvasTimelineDate(allocation.startYear, allocation.startMonth, allocation.startDay);
		const endDate = createCanvasTimelineDate(allocation.endYear, allocation.endMonth, allocation.endDay);
		const totalHours = getAllocationTotal(allocation) / 60;

		if (!areItemDatesValid(startDate, endDate)) return;

		let isDisabledIdleTime = false;
		if (idleTimeId) {
			isDisabledIdleTime = data.idleTimes.find(idleTime => idleTime.id === idleTimeId).disabled;
		}

		const isOwnAllocation = allocation.personId === data.viewer.actualPersonId;
		const isAllocationDisabled = !hasPermission(PERMISSION_TYPE.ALLOCATION_UPDATE) || isDisabled || isDoneOrHalted || isDisabledIdleTime
			|| (!isOwnAllocation && allocation.registerTime && !hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_UPDATE_ALL));

		this.updateWithData({
			groupId,
			color,
			startDate,
			endDate,
			allocation,
			totalHours,
			intl,
			draggableType:
				isAllocationDisabled
					? undefined
					: DRAGGABLE_TYPE.DATE_AND_GROUP,
			person,
			isDisabled,
			isDoneOrHalted,
			onMoveAttempt: isDoneOrHalted ? this.pageComponent.onDoneOrHaltedMoveAttempt.bind(this.pageComponent) : undefined,
			onMouseEnter: this.onProjectAllocationMouseEnter.bind(this),
			onMouseLeave: this.onProjectAllocationMouseLeave.bind(this),
			onMouseMove: this.onProjectAllocationMouseMove.bind(this),
			onMoving: this.onProjectAllocationMoving.bind(this),
			onMoveEnd: this.onProjectAllocationMoveEnd.bind(this),
			updateFreeDragData: this.pageComponent.updateAllocationFreeDragData.bind(this.pageComponent),
			onClick: this.onProjectAllocationClick.bind(this, allocation, person, groupId, isDisabled, isDoneOrHalted),
		});
	}

	onProjectAllocationMoveEnd(item, group, initialGroup, dragData) {
		let targetGroup = group;
		if (!targetGroup) {
			return;
		}
		if (targetGroup.groupType === GROUP_TYPE.PEOPLE_SCHEDULING_PERSON && !item.data.allocation.idleTimeId) {
			//Project group was not found
			const isConnectedProject = !!item.data.allocation.projectGroupId;

			const data = this.pageComponent.getData();
			const project = isConnectedProject
				? data.projectGroups.find(projectGroup => projectGroup.id === item.data.allocation.projectGroupId)
				: data.projects.find(project => project.id === item.data.allocation.projectId);

			targetGroup = this.pageComponent.getNewProjectGroup(group.data, project);
			dragData.innermostVisibleParentGroup = group.expanded ? targetGroup : group;
			item.groupId = targetGroup.id;
		}

		const startDate = getMomentFromCanvasTimelineDate(item.startDate);
		const endDate = getMomentFromCanvasTimelineDate(item.endDate);

		const onSuccess = res => {
			if (res.updateAllocation && !res.updateAllocation.errors) {
				dispatch(EVENT_ID.SCHEDULING_MODAL_MUTATION_SUCCESS, res);
			} else {
				//if there is an issue we want to redraw to the previous value
				const initalStartDate = getMomentFromCanvasTimelineDate(dragData.initialStartDate);
				const intialEndDate = getMomentFromCanvasTimelineDate(dragData.initialEndDate);

				item.data.allocation.startYear = initalStartDate.year();
				item.data.allocation.startMonth = initalStartDate.month() + 1;
				item.data.allocation.startDay = initalStartDate.date();
				item.data.allocation.endYear = intialEndDate.year();
				item.data.allocation.endMonth = intialEndDate.month() + 1;
				item.data.allocation.endDay = intialEndDate.date();
				item.updateWithAllocation(item.data.allocation);
				if (this.pageComponent.heatmapCache.get(item.data.allocation.personId)) {
					this.pageComponent.heatmapCache.get(item.data.allocation.personId).needsRecalculation = true;
				}
				this.pageComponent.clearCompanyUtilizationCache();
			}
		};
		updateAllocation(
			{
				id: item.data.allocation.id,
				personId: targetGroup.data.personId,
				startYear: startDate.year(),
				startMonth: startDate.month() + 1,
				startDay: startDate.date(),
				endYear: endDate.year(),
				endMonth: endDate.month() + 1,
				endDay: endDate.date(),
			},
			onSuccess
		);

		item.data.allocation.personId = targetGroup.data.personId;
		item.data.allocation.startYear = startDate.year();
		item.data.allocation.startMonth = startDate.month() + 1;
		item.data.allocation.startDay = startDate.date();
		item.data.allocation.endYear = endDate.year();
		item.data.allocation.endMonth = endDate.month() + 1;
		item.data.allocation.endDay = endDate.date();
		item.updateWithAllocation(item.data.allocation);
		if (this.pageComponent.heatmapCache.get(item.data.allocation.personId)) {
			this.pageComponent.heatmapCache.get(item.data.allocation.personId).needsRecalculation = true;
		}
		this.pageComponent.clearCompanyUtilizationCache();
	}

	onProjectAllocationMouseEnter(eventData, event) {
		const {
			pageComponent,
			data: {allocation},
		} = this;
		pageComponent.setState({
			hoverX: event.clientX,
			hoverY: event.clientY,
			showDetailBox: false,
		});
		setTimeout(() => {
			pageComponent.setState({
				detailBoxLeft: eventData.x,
				detailBoxX: event.clientX,
				detailBoxY: eventData.y,
				detailBoxTop: eventData.y - 2,
				detailBoxData: {task: null, allocation},
			});
			if (pageComponent.state.hoverX === event.clientX && pageComponent.state.hoverY === event.clientY) {
				pageComponent.setState({showDetailBox: true, detailBoxX: event.clientX});
			}
		}, 200);
	}

	onProjectAllocationMouseLeave() {
		this.pageComponent.setState({showDetailBox: false, hoverX: null, hoverY: null, detailBoxData: null});
	}

	onProjectAllocationMouseMove(eventData, event) {
		const {pageComponent} = this;
		pageComponent.setState({
			hoverX: event.clientX,
			hoverY: event.clientY,
			showDetailBox: false,
		});
		setTimeout(() => {
			if (
				pageComponent.state.hoverX === event.clientX &&
				pageComponent.state.hoverY === event.clientY &&
				pageComponent.state.detailBoxData
			) {
				pageComponent.setState({showDetailBox: true, detailBoxX: event.clientX});
			}
		}, 200);
	}

	onProjectAllocationMoving(item, d1, d2, d3, dragData) {
		const {pageComponent} = this;
		if (this.pageComponent.heatmapCache.get(item.data.allocation.personId)) {
			pageComponent.heatmapCache.get(item.data.allocation.personId).needsRecalculation = true;
		}
		const startDate = getMomentFromCanvasTimelineDate(dragData.newStartDate || item.startDate);
		const endDate = getMomentFromCanvasTimelineDate(dragData.newEndDate || item.endDate);
		item.data.allocation.startYear = startDate.year();
		item.data.allocation.startMonth = startDate.month() + 1;
		item.data.allocation.startDay = startDate.date();
		item.data.allocation.endYear = endDate.year();
		item.data.allocation.endMonth = endDate.month() + 1;
		item.data.allocation.endDay = endDate.date();
		item.data.totalHours = getAllocationTotal(item.data.allocation) / 60;

		return true;
	}

	onProjectAllocationClick(allocation, person, groupId, isDisabled, isDoneOrHalted) {
		const {pageComponent} = this;

		if (!isDisabled && !isDoneOrHalted) {
			const data = pageComponent.getData();

			showModal({
				type: MODAL_TYPE.CANVAS_CREATE,
				projectGroups: data.projectGroups,
				projects: data.projects,
				idleTimes: data.idleTimes,
				projectPersons: data.projectPersons,
				actualPersonId: data.viewer.actualPersonId,
				persons: data.persons,
				selectedPersonId: person.id,
				milestones: data.phases,
				company: data.company,
				holidayCalendars: data.holidayCalendars,
				holidayCalendarEntries: data.holidayCalendarEntries,
				allocation: allocation,
				roles: data.roles,
				teams: data.teams,
				teamPersons: data.teamPersons,
				removeLowHigh: data.viewer.availableFeatureFlags.some(flag => flag.key === 'remove_low_high'),
			});

			tracking.trackEvent('Open Allocation Modal');
		}
	}

	draw(canvasContext, x, y, width, canvasWidth) {
		const {data, height, isBeingDragged} = this;
		const {color, totalHours, allocation, intl, isDisabled} = data;
		//draw background colored rectangle
		drawRectangle(canvasContext, x, y, width, height, {
			backgroundColor: isDisabled ? '#d4d4d4' : color,
			borderRadius: 10,
		});
		if (isBeingDragged || isDisabled) return; //Don't draw text if the item is being dragged or if it is being viewed by project manager that does not have access to project

		let showPerDayText = true;
		let minutesPerDay = null;
		const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
		for (const dayName of dayNames) {
			if (allocation[dayName]) {
				if (minutesPerDay) {
					if (allocation[dayName] !== minutesPerDay) {
						showPerDayText = false;
						break;
					}
				} else {
					minutesPerDay = allocation[dayName];
				}
			}
		}

		let text = '';
		if (showPerDayText) {
			text += `${intl.formatMessage(
				{id: 'scheduling.time_per_day'},
				{time: Util.convertMinutesToFullHour(minutesPerDay, intl)}
			)} | `;
		}
		text += Util.convertMinutesToFullHour(totalHours * 60, intl);
		canvasContext.font = '400 12px ' + Util.getFontFamily();
		const textWidth = canvasContext.measureText(text).width;
		const textMargin = 16;
		const textXCoord =
			x + width > canvasWidth ? canvasWidth - (textMargin + textWidth) : x + width - (textMargin + textWidth);
		if (width > 32 && x + 16 < textXCoord) {
			canvasContext.fillStyle = getTextColor(color);
			const formattedTaskName = getTrimmedText(canvasContext, text, width - 32);
			canvasContext.fillText(formattedTaskName, textXCoord, y + 21);
			// Show name of allocation
			if (allocation.idleTimeName || allocation.displayName) {
				const textMargin = 16;
				const visibleItemStartX = x > 0 ? x : 0;
				const visibleItemEndX = x + width > canvasWidth ? canvasWidth : x + width;
				const visibleItemWidth = visibleItemEndX - visibleItemStartX;
				canvasContext.fillText(
					getTrimmedText(
						canvasContext,
						allocation.idleTimeName || allocation.displayName,
						visibleItemWidth - textMargin * 2 - textWidth - 20
					),
					textMargin + visibleItemStartX,
					y + 21
				);
			}
		}
	}
}
